import { isAfter, isBefore, startOfDay } from "date-fns";
import { v4 } from "uuid";

import { CalculationParameterEntryStatus, CalculationParameterKey, CalculationParameterResponse } from "@/api";

import { ParameterEntryFormValues, ParameterFormValues } from "./CalculationParametersSettings.types";

export function isEffectiveFromEditable(entry: ParameterEntryFormValues): boolean {
  return entry.status === null || entry.status === CalculationParameterEntryStatus.Future;
}

export function isPersistedFutureEntry(entry: ParameterEntryFormValues): boolean {
  return entry.id !== null && entry.status === CalculationParameterEntryStatus.Future;
}

export function toFormValues(parameter: CalculationParameterResponse | null): ParameterFormValues {
  return {
    entries: (parameter?.entries ?? []).map((entry) => ({
      rowKey: entry.id,
      id: entry.id,
      effectiveFrom: entry.effectiveFrom,
      value: entry.value,
      status: entry.status,
    })),
  };
}

export function createEmptyEntry(): ParameterEntryFormValues {
  return { rowKey: v4(), id: null, effectiveFrom: null, value: null, status: null };
}

/** The value another parameter has on the given date, from its saved entries. */
export function savedValueOn(
  parameters: CalculationParameterResponse[],
  key: CalculationParameterKey,
  date: Date
): number | null {
  const day = startOfDay(date);
  const inEffect = (parameters.find((p) => p.key === key)?.entries ?? [])
    .filter((entry) => !isAfter(startOfDay(entry.effectiveFrom), day))
    .sort((a, b) => b.effectiveFrom.getTime() - a.effectiveFrom.getTime());

  return inEffect[0]?.value ?? null;
}

/** The dates an entry is checked against other parameters on: its own start plus every change of those parameters while it is in effect. */
export function crossCheckDates(
  effectiveFrom: Date,
  ownEntries: ParameterEntryFormValues[],
  parameters: CalculationParameterResponse[],
  otherKeys: CalculationParameterKey[]
): Date[] {
  const start = startOfDay(effectiveFrom);
  const end = ownEntries
    .flatMap((entry) => (entry.effectiveFrom == null ? [] : [startOfDay(entry.effectiveFrom)]))
    .filter((date) => isAfter(date, start))
    .sort((a, b) => a.getTime() - b.getTime())[0];

  const changes = parameters
    .filter((parameter) => otherKeys.includes(parameter.key))
    .flatMap((parameter) => parameter.entries.map((entry) => startOfDay(entry.effectiveFrom)))
    .filter((date) => isAfter(date, start) && (end === undefined || isBefore(date, end)));

  return [start, ...changes];
}
